type BottomNavButtonProps = {
  children: React.ReactNode;
  variant:
    | "default"
    | "purple"
    | "orange"
    | "green"
    | "beige"
    | "red"
    | "yellow"
    | "garnet"
    | "night"
    | "blue"
    | "gray";
  onClick?: () => void;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  type?: "button" | "submit" | "reset";
  className?: string;
};

const variantClasses: Record<
  string,
  {
    bg: string;
    hover: string;
    border: string;
    shadow: string;
    after: string;
    decorative1: string;
    decorative2: string;
  }
> = {
  default: {
    bg: "bg-pk-purple-2",
    hover: "hover:bg-pk-purple-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
  orange: {
    bg: "bg-pk-orange-2",
    hover: "hover:bg-pk-orange-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
  green: {
    bg: "bg-pk-green-2",
    hover: "hover:bg-pk-green-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/50",
    decorative2: "bg-pk-cream-2/80",
  },
  beige: {
    bg: "bg-pk-beige-2",
    hover: "hover:bg-pk-beige-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
  red: {
    bg: "bg-pk-red-2",
    hover: "hover:bg-pk-red-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
  yellow: {
    bg: "bg-pk-yellow-2",
    hover: "hover:bg-pk-yellow-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/85",
    decorative2: "bg-pk-cream-2/90",
  },
  garnet: {
    bg: "bg-pk-garnet-2",
    hover: "hover:bg-pk-garnet-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },

  night: {
    bg: "bg-pk-night-2",
    hover: "hover:bg-pk-night-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
  blue: {
    bg: "bg-pk-blue-2",
    hover: "hover:bg-pk-blue-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
  gray: {
    bg: "bg-pk-gray-2",
    hover: "hover:bg-pk-gray-2 hover:after:border-pk-beige-5/60",
    border: "border-black",
    shadow: "shadow-[0_3px_0_rgba(0,0,0,1)]",
    after: "after:border-pk-beige-5/30",
    decorative1: "bg-pk-cream-2/20",
    decorative2: "bg-pk-beige-5/40",
  },
};

export const Button = ({
  children,
  variant,
  onClick,
  loading = false,
  disabled = false,
  type = "button",
  className,
}: BottomNavButtonProps) => {
  const currentVariationClasses = (variantClasses[variant] ??
    variantClasses.default)!;

  const handleClick = () => {
    if (!loading && !disabled) {
      onClick?.();
    }
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      className={`${className} ${`
          relative overflow-hidden
          flex flex-col items-center justify-end gap-1
          px-3 py-1 xs:py-2

          rounded-[10px] rounded-tl-none
          ${currentVariationClasses.bg}

          border-[4px] ${currentVariationClasses.border}
          shadow-[0_3px_0_rgba(0,0,0,1)]
          ${
            !disabled && !loading
              ? "active:translate-y-[2px] active:shadow-none"
              : ""
          }

          transition-all duration-150
          ${disabled || loading ? "cursor-not-allowed" : "group cursor-pointer"}

          before:content-['']
          before:absolute before:inset-[-14px]
          before:bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.10),transparent_42%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.08),transparent_45%),repeating-linear-gradient(135deg,rgba(0,0,0,0.08)_0px,rgba(0,0,0,0.08)_2px,transparent_2px,transparent_6px)]
          before:pointer-events-none

          after:content-['']
          after:absolute after:inset-0 after:pointer-events-none
          after:border-[2px] ${currentVariationClasses.after}
          after:rounded-[8px] after:rounded-tl-none

          ${
            disabled || loading
              ? ""
              : `${currentVariationClasses.hover} hover:shadow-[0_3px_0_rgba(0,0,0,1),0_0_0_3px_rgba(140,90,255,0.18)]`
          }
        `}`}
    >
      {/* gothic “ears” / points */}
      <span
        className={`
            absolute -top-2 -left-1 w-4 h-4
            rotate-45
            ${currentVariationClasses.decorative1}
            pointer-events-none
          `}
      />

      <span
        className={`
            absolute -top-2 -right-1 w-4 h-4
            rotate-45
            scale-x-[-1]
            ${currentVariationClasses.decorative2}
            pointer-events-none
          `}
      />

      <div className={`relative w-full`}>{children}</div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/80">
          {/* Fantasy-themed loading spinner overlay */}
          <div className="relative w-6 h-6">
            {/* Outer ring */}
            <div className="absolute inset-0 rounded-full border-4 border-pk-gray-5 animate-spin border-t-transparent" />
            {/* Inner ring */}
            <div className="absolute inset-1 rounded-full border border-pk-beige-5/60 animate-spin animation-reverse" />
            {/* Center dot */}
            <div className="absolute top-1/2 left-1/2 w-1 h-1 -translate-x-1/2 -translate-y-1/2 bg-pk-beige-5 rounded-full animate-pulse" />
          </div>
        </div>
      )}

      {disabled && !loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none backdrop-blur-[1px] bg-black/60 rounded-[10px] rounded-tl-none">
          {/* Lock icon overlay */}
          <svg
            className="w-6 h-6 text-pk-gray-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
      )}
    </button>
  );
};
